/**
 * Invoice statuses, shared by every screen that draws one.
 *
 * Moved down here when `invoice-management` became the second feature to need it — `subscriptions`
 * shows the same statuses on a subscription's invoice list. A shared shape that lives in one
 * feature and is copied into the next is how two screens drift apart, which is exactly what
 * happened to the subscription status maps this codebase already had two of.
 *
 * ### The API sends this two different ways
 *
 * The subscription detail's `invoices[]` carries a **numeric** `status` (1–12). The invoice
 * report's list and detail carry a **string** `status_label` alongside a numeric `status`.
 * `invoiceStatus()` takes either and answers the same way.
 *
 * The numeric mapping is the legacy's, and its own comment records how far it was verified:
 * "Confirmed from the /summary response: 1=draft, 2=open, 3=paid. Other numeric codes (5, 9,
 * 10, 12...) show up in /summary but without a label." Codes 1–3 are confirmed; the rest are
 * read off `statusCodeLabels` and should be treated as probable, not settled.
 */

export type InvoiceStatusTone = 'success' | 'warning' | 'error' | 'info' | 'neutral';

type InvoiceStatusEntry = { label: string; tone: InvoiceStatusTone };

/** By string key, which is what `status_label` holds. Both spellings of "cancelled" resolve. */
export const INVOICE_STATUS_BY_LABEL: Record<string, InvoiceStatusEntry> = {
  draft: { label: 'Draft', tone: 'neutral' },
  open: { label: 'Open', tone: 'info' },
  // The API uses `unpaid` and `open` interchangeably for the same state.
  unpaid: { label: 'Open', tone: 'info' },
  paid: { label: 'Paid', tone: 'success' },
  uncollectible: { label: 'Uncollectible', tone: 'error' },
  void: { label: 'Void', tone: 'neutral' },
  price_locked: { label: 'Price locked', tone: 'info' },
  partial_refund: { label: 'Partial refund', tone: 'warning' },
  refunded: { label: 'Refunded', tone: 'info' },
  failed: { label: 'Failed', tone: 'error' },
  ready_for_checkout: { label: 'Ready for checkout', tone: 'info' },
  canceled: { label: 'Canceled', tone: 'neutral' },
  cancelled: { label: 'Canceled', tone: 'neutral' },
  processing: { label: 'Processing', tone: 'info' },
};

/** By numeric code. 1–3 are confirmed against `/summary`; 4–12 are probable. */
export const INVOICE_STATUS_BY_CODE: Record<number, string> = {
  1: 'draft',
  2: 'open',
  3: 'paid',
  4: 'uncollectible',
  5: 'void',
  6: 'price_locked',
  7: 'partial_refund',
  8: 'refunded',
  9: 'failed',
  10: 'ready_for_checkout',
  11: 'canceled',
  12: 'processing',
};

/**
 * How an invoice status reads, from either a code or a label.
 *
 * An unknown value is shown rather than hidden: a status nobody has mapped yet is still the
 * answer to "what state is this invoice in", and a dash there is a lie.
 */
export const invoiceStatus = (status: number | string | null | undefined): InvoiceStatusEntry => {
  if (status === null || status === undefined || status === '') {
    return { label: '-', tone: 'neutral' };
  }

  const key =
    typeof status === 'number'
      ? (INVOICE_STATUS_BY_CODE[status] ?? String(status))
      : status.toLowerCase();

  const known = INVOICE_STATUS_BY_LABEL[key];
  if (known) return known;

  return {
    label: key.slice(0, 1).toUpperCase() + key.slice(1).replace(/_/g, ' '),
    tone: 'neutral',
  };
};

/** The statuses a filter panel offers — one entry per distinct meaning. */
export const INVOICE_STATUS_OPTIONS = Object.entries(INVOICE_STATUS_BY_CODE).map(([code, key]) => ({
  value: code,
  label: INVOICE_STATUS_BY_LABEL[key]?.label ?? key,
}));
